import { isNullOrUndefined } from "csi/utility/coreUtility";
import React, { CSSProperties, ReactElement } from "react";
import { useDragLayer, XYCoord } from "react-dnd";
import { useDndStore } from "../store/dnd-store";

interface DragPreviewLayerProps<T> {
	/** offset */
	offset?: XYCoord;

	/** the preview component to render */
	previewComponent?: ReactElement | ((item: T | T[]) => ReactElement);

	/** the z index of the preview layer */
	zIndex?: number;
}

/**
 * DragPreviewLayer is a React component that provides a preview layer for a draggable item.
 * It uses the React DnD (Drag and Drop) hooks to monitor the current drag state and renders
 * a preview component at the cursor's position when an item is being dragged. The preview
 * is fully customizable through a `previewComponent` prop, which can be either a React element
 * or a function that returns a React element, allowing for dynamic rendering.
 */
const DragPreviewLayer = <T extends {}>({ offset, previewComponent, zIndex }: DragPreviewLayerProps<T>) => {
	const { multiSelectItemArray } = useDndStore();
	const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
		item: monitor.getItem(),
		currentOffset: monitor.getSourceClientOffset(),
		isDragging: monitor.isDragging(),
	}));

	if (!isDragging || !currentOffset) {
		return null;
	}

	const transform = `translate(${currentOffset.x}px, ${currentOffset.y}px)`;
	const style: CSSProperties = {
		left: offset ? offset.x : 0,
		pointerEvents: "none",
		position: "fixed",
		top: offset ? offset.y : 0,
		transform,
		zIndex: isNullOrUndefined(zIndex) ? 1 : zIndex,
	};

	return (
		<div style={style}>
			{typeof previewComponent === "function" ? (
				previewComponent(multiSelectItemArray.length ? multiSelectItemArray : item)
			) : previewComponent}
		</div>
	);
};

export default DragPreviewLayer;
